import LegalDocumentPage from "@/components/legal/LegalDocumentPage";
import { RADAR_LEGAL_ENTITY, RADAR_JURISDICTION } from "@/lib/legalEntity";

export default function TermsOfServicePage() {
  return (
    <LegalDocumentPage
      title="Terms of Service"
      description="Terms of Service for Radar by S.C. Aquivo Labs SRL (Romania): governing law, liability, and use of the service."
      canonical="/terms"
    >
      <p>
        These Terms of Service (“Terms”) govern your access to and use of Radar, including the website at
        radar.aquivolabs.com and the Radar application for Shopify (“Service”). The Service is provided by{" "}
        {RADAR_LEGAL_ENTITY} (“Aquivo Labs,” “we,” “us,” or “our”). By accessing or using the Service, you agree to these
        Terms. If you do not agree, do not use the Service.
      </p>

      <h2>Parties</h2>
      <p>
        You are entering into these Terms with {RADAR_LEGAL_ENTITY}, a Romanian limited liability company (societate cu
        răspundere limitată) that owns and operates Radar and is incorporated under the laws of {RADAR_JURISDICTION}.
        “You” means the individual or entity that registers for or uses the Service, including anyone acting on your
        behalf (for example, employees or contractors).
      </p>

      <h2>Eligibility</h2>
      <p>
        You must be legally able to enter a binding contract in your jurisdiction. If you use the Service on behalf of
        an organization, you represent that you have authority to bind that organization to these Terms.
      </p>

      <h2>Shopify</h2>
      <p>
        If you access Radar through Shopify, your use of Shopify’s platform is also governed by Shopify’s terms and
        policies. We are not responsible for Shopify’s services. Uninstalling the app or losing access to Shopify may
        affect your ability to use Radar.
      </p>

      <h2>Accounts and access</h2>
      <p>
        You are responsible for maintaining the confidentiality of your credentials and for activity under your account.
        You must provide accurate registration information and notify us promptly of unauthorized use through the
        support channel listed on the Radar listing in the Shopify App Store.
      </p>

      <h2>The Service</h2>
      <p>
        Radar provides competitor intelligence and related tools (for example, monitoring ads, pricing signals, or
        storefront data depending on your plan and configuration). Features may change over time. We do not guarantee
        uninterrupted or error-free operation, or that any particular competitor or data source will always be
        available or accurate.
      </p>

      <h2>Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>Use the Service in violation of law or third-party rights;</li>
        <li>Attempt to probe, scan, or test vulnerabilities, or breach security or authentication measures;</li>
        <li>Interfere with or disrupt the Service or other users’ use;</li>
        <li>Use automated means to access the Service in a way that overloads or harms our systems (except as we
          explicitly permit via API or documented integration);</li>
        <li>Resell, sublicense, or redistribute the Service without our written consent;</li>
        <li>Use intelligence obtained through the Service to harass, stalk, or harm individuals or to engage in unfair
          or deceptive practices.</li>
      </ul>

      <h2>Intellectual property</h2>
      <p>
        We and our licensors own the Service, including software, branding, and content we provide. Subject to these
        Terms, we grant you a limited, non-exclusive, non-transferable right to use the Service for your internal
        business purposes during your subscription or trial. You retain rights in your own data; you grant us a license
        to host, process, and display your data as needed to provide and improve the Service.
      </p>

      <h2>Third-party data and APIs</h2>
      <p>
        The Service may rely on third-party sources (including ad libraries, public web data, or Shopify APIs). Those
        sources have their own terms and availability. You are responsible for your use of outputs in compliance with
        applicable law and platform rules.
      </p>

      <h2>Fees and trials</h2>
      <p>
        Paid plans, billing cycles, taxes (including VAT or similar indirect taxes where applicable under Romanian and EU
        rules), and free trials are described at checkout or in the app. Unless stated otherwise, fees are non-refundable
        except where required by law. We may change pricing with reasonable notice where permitted by contract and law.
      </p>

      <h2>Disclaimer of warranties</h2>
      <p>
        THE SERVICE IS PROVIDED “AS IS” AND “AS AVAILABLE.” TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL
        WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT
        INTELLIGENCE OR RECOMMENDATIONS WILL ACHIEVE ANY PARTICULAR BUSINESS RESULT.
      </p>

      <h2>Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR LOSS OF PROFITS, DATA, OR GOODWILL. OUR TOTAL LIABILITY FOR ANY CLAIM
        ARISING OUT OF THESE TERMS OR THE SERVICE WILL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU PAID US FOR THE
        SERVICE IN THE TWELVE MONTHS BEFORE THE CLAIM OR (B) ONE HUNDRED U.S. DOLLARS, EXCEPT WHERE LIABILITY CANNOT BE
        LIMITED BY LAW. If you are a consumer under mandatory rules of Romanian law or other law applicable to you,
        nothing in these Terms limits any non-waivable rights or remedies you have under those rules.
      </p>

      <h2>Indemnity</h2>
      <p>
        You will defend and indemnify us and our affiliates, officers, and employees against third-party claims,
        damages, and costs (including reasonable attorneys’ fees) arising from your use of the Service, your data, or
        your violation of these Terms or applicable law, except to the extent caused by our gross negligence or willful
        misconduct.
      </p>

      <h2>Suspension and termination</h2>
      <p>
        We may suspend or terminate access if you materially breach these Terms, if we must do so for legal or security
        reasons, or if we discontinue the Service with reasonable notice where practicable. Provisions that by their
        nature should survive (including disclaimers, limitations, indemnity, and governing law) will survive
        termination.
      </p>

      <h2>Governing law and disputes</h2>
      <p>
        These Terms are governed by the laws of {RADAR_JURISDICTION}, without regard to conflict-of-law rules that would
        apply the law of another jurisdiction. Subject to mandatory provisions of EU law and any non-waivable consumer
        protections that apply to you (including, where relevant, Romanian consumer protection rules), the courts of{" "}
        {RADAR_JURISDICTION} shall have jurisdiction over disputes arising from or relating to these Terms or the
        Service. If you qualify as a consumer in the European Union, you may also benefit from mandatory rules that allow
        you to bring proceedings in your country of residence.
      </p>

      <h2>Changes</h2>
      <p>
        We may modify these Terms by posting an updated version on this page and updating the “Last updated” date. If we
        make material changes, we will provide notice as required by law or through the Service. Continued use after
        changes become effective constitutes acceptance of the revised Terms.
      </p>

      <h2>Privacy</h2>
      <p>
        Our collection and use of personal information is described in our Privacy Policy. By using the Service, you
        acknowledge that policy.
      </p>
    </LegalDocumentPage>
  );
}
